// Studby — student dashboard. Shows usage stats + recent quiz scores.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import { Camera, FileText, MessageCircle, CheckCircle2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrialBanner } from "@/components/Paywall";

const STAT_CARDS = [
  { key: "questions_solved", label: "Questions solved", icon: Camera },
  { key: "quizzes_taken", label: "Quizzes taken", icon: FileText },
  { key: "chat_messages", label: "Tutor chats", icon: MessageCircle },
];

const QUICK = [
  { to: "/app/solve", label: "Solve a photo", icon: Camera },
  { to: "/app/quiz", label: "Make a quiz", icon: FileText },
  { to: "/app/tutor", label: "Ask the tutor", icon: MessageCircle },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const chartData = (stats?.recent_scores || []).map((s, i) => ({
    name: `Quiz ${i + 1}`,
    score: s.score,
  }));

  return (
    <div>
      <TrialBanner />

      <div className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">
          Hey {user?.name?.split(" ")[0] || "there"} 👋
        </h1>
        <p className="text-muted-foreground mt-1">Here's how your studying is going.</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-8">
        {STAT_CARDS.map((c, i) => {
          const Icon = c.icon;
          return (
            <motion.div
              key={c.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="bg-card border border-border rounded-2xl p-4 sm:p-5"
              data-testid={`stat-${c.key}`}
            >
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary text-primary mb-3">
                <Icon size={18} aria-hidden="true" />
              </span>
              <p className="font-heading text-2xl sm:text-3xl font-extrabold">
                {stats ? stats[c.key] : "—"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Recent quiz scores */}
      <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 mb-8">
        <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
          <CheckCircle2 size={18} className="text-accent" aria-hidden="true" /> Recent quiz scores
        </h2>
        {chartData.length > 0 ? (
          <div className="h-52" data-testid="scores-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} width={30} />
                <Tooltip cursor={{ fill: "hsl(150 20% 96%)" }} />
                <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill="hsl(160 84% 39%)" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No quizzes yet — generate one from your notes to see your scores here.
          </p>
        )}
      </div>

      {/* Quick actions */}
      <h2 className="font-heading text-lg font-bold mb-3">Jump back in</h2>
      <div className="grid sm:grid-cols-3 gap-3">
        {QUICK.map((q) => {
          const Icon = q.icon;
          return (
            <Link
              key={q.to}
              to={q.to}
              data-testid={`quick-${q.to.split("/").pop()}`}
              className="flex items-center justify-between bg-card border border-border rounded-xl px-4 py-4 hover:border-accent transition-colors group"
            >
              <span className="flex items-center gap-3 font-medium">
                <Icon size={18} className="text-primary" aria-hidden="true" /> {q.label}
              </span>
              <ArrowRight size={16} className="text-muted-foreground group-hover:text-accent transition-colors" aria-hidden="true" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
